<template>
  <div class="basis-overview text-dark">
    <div class="basis-overview__header">
      <h1>Basis Hours</h1>
      <div class="basis-overview__dates">
        <div class="do-spinner" v-if="isLoading"></div>
        <Tabs
          :tabs="[{id: 'hours', title: 'Hours'}, {id: 'minutes', title: 'Minutes'}]"
          :selected="theSelectedTab"
          @select="theSelectedTab = $event">
        </Tabs>
        <button class="do-bttn" @click="asyncLoadXLSXAndDownload" :class="{'do-bttn--loading': isDownloading}">Download
          to excel
        </button>
        <div class="do-button__multiple">
          <button class="do-button__multiple__bttn icon-arrow-left4" @click="setPrevWeek"></button>
          <button class="do-button__multiple__bttn" :class="{'bttn--disabled': isThisWeek}" @click="setThisWeek">This
            week
          </button>
          <button class="do-button__multiple__bttn icon-arrow-right4" @click="setNextWeek"></button>
        </div>
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </DatePickerRange>
      </div>
    </div>
    <div class="basis-overview__body">
      <table class="d-table">
        <thead>
        <tr>
          <th v-for="col in col1"
              :rowspan="col.rowspan ? col.rowspan : 1"
              :colspan="col.colspan ? col.colspan : 1"
              :style="{backgroundColor: col.color ? col.color : null}">
            <div>
              {{ col.name }}
            </div>
          </th>
        </tr>
        <tr>
          <th v-for="col in col2"
              :rowspan="col.rowspan ? col.rowspan : 1"
              :colspan="col.colspan ? col.colspan : 1"
              :style="{backgroundColor: col.color ? col.color : null}">
            {{ col.name }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="user in usersWithVEvents">
          <td>{{ user.employee_id }}</td>
          <td>{{ user.name }}</td>
          <td @click.stop="openEdit(user, user.v_presence, 'weeklyHours', $event)">
            {{ user.v_presence }}
          </td>
          <td>{{ user.v_subject }}</td>
          <td>{{ user.v_break }}</td>
          <td>{{ user.v_tasks }}</td>
          <td>{{ user.v_preparation }}</td>
          <td>{{ user.v_substitute_teaching }}</td>
          <td>{{ user.v_substitute_not_teaching }}</td>
          <td>{{ user.v_hours_left }}</td>
          <td>{{ user.v_absence }}</td>
        </tr>
        </tbody>
      </table>
      <div class="popper_child" ref="popper_child" v-if="theEditNumberUser">
        <transition name="scale">
          <div class="number__input-opened" v-if="isDropdownPopperDone">
            <input type="number"
                   v-model.lazy="theEditNumberNumber"
                   v-focus
                   v-on-clickaway="closeEditing"
                   @keyup.enter="doneEditing"/>
            <button class="bttn bttn--primary" :class="{'bttn--small--loading': isUpdatingNumber}"
                    @click.stop="doneEditing">
              <span v-if="!isUpdatingNumber">{{ $t('vue2.common.save') }}</span>
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import DatePickerRange from '../datepicker/DatePickerRange.vue'
import {mixin as clickaway} from 'vue-clickaway'
import Tabs from '../common/Tabs.vue' // eslint-disable-line no-unused-vars

let XLSX = null

export default {
  components: {Tabs, DatePickerRange},
  mixins: [clickaway],
  directives: {
    focus: {
      // directive definition
      inserted: function (el) {
        el.focus()
        el.select()
      }
    }
  },
  data () {
    return {
      theSelectedTab: 'hours',
      isDownloading: false,
      theEditNumberUser: null,
      theEditNumberType: '',
      theEditNumberNumber: 0,
      isDropdownPopperDone: false,
      isUpdatingNumber: false,
      startDate: moment().startOf('isoWeek'),
      endDate: moment().endOf('isoWeek'),
      col1: [
        {name: 'Employee id', rowspan: 2},
        {name: 'Name', rowspan: 2},
        {name: 'Working hours', color: '#f7f9fe'},
        {name: 'Teaching', colspan: 2, color: '#f2f2ff'},
        {name: 'Tasks', color: '#fffbf1'},
        {name: 'Preparation', color: '#fef7f5'},
        {name: 'Substitute', colspan: 2, color: '#f2fafb'},
        {name: 'Hours left', color: '#fff'},
        {name: 'Absent', color: '#f1f1f1'}
      ],
      col2: [
        {name: 'Working hours', color: '#f7f9fe'},
        {name: 'Subject', color: '#f2f2ff'},
        {name: 'Breaks', color: '#f2f2ff'},
        {name: 'Not teaching', color: '#fffbf1'},
        {name: 'Preparation', color: '#fef7f5'},
        {name: 'Teaching', color: '#f2fafb'},
        {name: 'Breaks/not teaching', color: '#f2fafb'},
        {name: 'Hours left', color: '#fff'},
        {name: 'Absent', color: '#f1f1f1'}
      ],
      events: [],
      isLoading: false,
      eventsInUserCalendarIdHash: {},
      debounceTimer: null,
      clickCount: 0,
      clickCountTimer: null
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    users () {
      return this.$store.getters.usersSortedOnName
    },
    usersWithEmployeeId () {
      return this.users.filter(user => user.employee_id)
    },
    filteredCalendars () {
      return this.users && this.users.length > 0 ? this.users.map(user => user.user_calendar_id) : []
    },
    isThisWeek () {
      const start = moment(this.startDate).format('YYYY-MM-DD') === moment().startOf('isoWeek').format('YYYY-MM-DD')
      const end = moment(this.endDate).format('YYYY-MM-DD') === moment().endOf('isoWeek').format('YYYY-MM-DD')
      return start && end
    },
    usersWithVEvents () {
      const multiple = this.theSelectedTab === 'minutes' ? 1 : 60
      const formatTime = num => (num / multiple).toFixed(2)

      const getWeeklyHours = (user) => {
        return user.custom_data &&
          user.custom_data.schools &&
          user.custom_data.schools[this.schoolId] &&
          user.custom_data.schools[this.schoolId]['weeklyHours'] || 0
      }

      return this.users.map(user => {
        const calUser = this.eventsInUserCalendarIdHash && this.eventsInUserCalendarIdHash[user.user_calendar_id]
        const getCount = key => calUser && calUser[key] || 0

        const vPresence = getWeeklyHours(user)
        const vSubject = getCount('subject_count')
        const vBreak = getCount('break_count')
        const vTasks = getCount('additional_tasks_count')
        const vPreparation = getCount('preparation_count')
        const vAbsence = getCount('absence_count')
        const vSubstituteTeaching = getCount('substitute_teaching_count')
        const vSubstituteNotTeaching = getCount('substitute_not_teaching_count')

        const usedTime = vSubject + vBreak + vTasks + vPreparation + vSubstituteTeaching + vSubstituteNotTeaching
        const vTotal = vPresence - usedTime

        return {
          ...user,
          v_presence: formatTime(vPresence),
          v_subject: formatTime(vSubject),
          v_break: formatTime(vBreak),
          v_tasks: formatTime(vTasks),
          v_preparation: formatTime(vPreparation),
          v_substitute_teaching: formatTime(vSubstituteTeaching),
          v_substitute_not_teaching: formatTime(vSubstituteNotTeaching),
          v_hours_left: formatTime(vTotal),
          v_absence: formatTime(vAbsence)
        }
      })
    }
  },
  watch: {
    users () {
      this.readEvents()
    }
  },
  methods: {
    computeOverlapMinutes (startA, endA, startB, endB) {
      const sA = moment(startA)
      const eA = moment(endA)
      const sB = moment(startB)
      const eB = moment(endB)
      const latestStart = moment.max(sA, sB)
      const earliestEnd = moment.min(eA, eB)
      const diff = earliestEnd.diff(latestStart, 'minutes')
      return diff > 0 ? diff : 0
    },
    dataToExcel () {
      // Map usersWithVEvents to the format needed for Excel export
      const users = this.usersWithVEvents.map(user => {
        return {
          'employee_id': user.employee_id || null,
          'name': user.name,
          'work_time': parseFloat(this.humanizeNumber(user.v_presence)),
          'subject': parseFloat(user.v_subject),
          'breaks': parseFloat(user.v_break),
          'tasks': parseFloat(user.v_tasks),
          'preparation': parseFloat(user.v_preparation),
          'substitute_teaching': parseFloat(user.v_substitute_teaching),
          'substitute_not_teaching': parseFloat(user.v_substitute_not_teaching),
          'hours_left': parseFloat(user.v_hours_left),
          'absence': parseFloat(user.v_absence)
        }
      })

      // Define base headers
      const baseHeaders = {
        'employee_id': 'Employee ID',
        'name': 'Name',
        'work_time': 'Work Time',
        'subject': 'Subject',
        'breaks': 'Breaks',
        'tasks': 'Tasks',
        'preparation': 'Preparation',
        'substitute_teaching': 'Substitute Teaching',
        'substitute_not_teaching': 'Substitute Not Teaching',
        'hours_left': 'Hours Left',
        'absence': 'Absence'
      }

      // Prepare the first header row (for merging purposes)
      const headersRow1 = Object.keys(baseHeaders).reduce((accumulator, key) => {
        accumulator[key] = '' // Fill first header row with empty strings for merging
        return accumulator
      }, {})

      // Second row with actual headers
      const headersRow2 = {...baseHeaders}

      // Add header rows at the top of the users array
      users.unshift(headersRow2, headersRow1)

      return users
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.download()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.download()
          }
        }, 500)
      }
    },
    download () {
      // Convert your data into a sheet format, skipping the default header
      let data = XLSX.utils.json_to_sheet(this.dataToExcel(), {skipHeader: true})

      // Define custom headers
      data.A1 = {t: 's', v: 'Employee id'}
      data.B1 = {t: 's', v: 'Navn'}
      data.C1 = {t: 's', v: 'Working hours'}

      // Teaching section headers
      data.D1 = {t: 's', v: 'Teaching'}      // Main header for Teaching
      data.D2 = {t: 's', v: 'Subject'}       // Sub-header for Subject
      data.E2 = {t: 's', v: 'Breaks'}        // Sub-header for Breaks

      // Tasks and Preparation columns
      data.F1 = {t: 's', v: 'Tasks'}
      data.G1 = {t: 's', v: 'Preparation'}

      // Substitute section headers
      data.H1 = {t: 's', v: 'Substitute'}    // Main header for Substitute
      data.H2 = {t: 's', v: 'Teaching'}      // Sub-header for Substitute Teaching
      data.I2 = {t: 's', v: 'Not Teaching'}  // Sub-header for Substitute Not Teaching

      // Additional columns for Hours Left and Absence
      data.J1 = {t: 's', v: 'Hours Left'}
      data.K1 = {t: 's', v: 'Absence'}

      // Merge the header cells (s - start, e - end, r - row, c - col, 0 based)
      const merge = [
        {s: {r: 0, c: 0}, e: {r: 1, c: 0}}, // Employee ID (A1:A2)
        {s: {r: 0, c: 1}, e: {r: 1, c: 1}}, // Name (B1:B2)
        {s: {r: 0, c: 2}, e: {r: 1, c: 2}}, // Work Time (C1:C2)

        // Teaching header (spanning two columns: Subject, Breaks)
        {s: {r: 0, c: 3}, e: {r: 0, c: 4}}, // Teaching header (D1:E1)

        // Substitute header (spanning two columns: Substitute Teaching, Substitute Not Teaching)
        {s: {r: 0, c: 7}, e: {r: 0, c: 8}} // Substitute header (H1:I1)

        // The rest columns (Tasks, Preparation, Hours Left, Absence) are individual and don't need merging
      ]

      // Assign the merge configuration to the sheet
      data['!merges'] = merge

      // Create a new workbook and append the sheet
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'data')

      // Write the file to download
      XLSX.writeFile(wb, 'docendo_tildel_timer.xlsx')

      // Reset the downloading state
      this.isDownloading = false
    },
    humanizeNumber (num) {
      return parseFloat(num).toFixed(1)
    },
    closeEditing () {
      this.isDropdownPopperDone = false
      this.theEditNumberUser = null
      this.theEditNumberType = ''
      this.theEditNumberNumber = 0
    },
    doneEditing () {
      console.log('doneEditing')
      this.updateUser(this.theEditNumberUser, this.theEditNumberNumber)
    },
    createPopper (parentPopper, childPopper) {
      parentPopper.appendChild(childPopper)
    },
    setOpenEdit (obj) {
      this.isDropdownPopperDone = false
      this.theEditNumberUser = obj.user
      this.theEditNumberType = obj.type
      this.theEditNumberNumber = obj.number
      this.$nextTick(_ => {
        this.createPopper(obj.evt.target, this.$refs.popper_child)
        this.isDropdownPopperDone = true
      })
    },
    openEdit (user, num, type, evt) {
      const numberInfo = {
        user: user,
        number: num,
        type: type,
        evt: evt
      }
      this.setOpenEdit(numberInfo)
    },
    updateUser (user, val) {
      this.isUpdatingNumber = true
      const change = 'weeklyHours'
      let customData = user.custom_data
      let changes = {}
      if (!customData['schools']) {
        customData.schools = {[this.schoolId]: {[change]: val}}
      }
      if (customData['schools'] && !customData['schools'][this.schoolId]) {
        customData['schools'][this.schoolId] = {[change]: val}
      }
      if (customData['schools'] && customData['schools'][this.schoolId]) {
        if (!val || val.length < 1) {
          delete customData['schools'][this.schoolId][[change]]
        } else {
          customData['schools'][this.schoolId][[change]] = val
        }
      }
      changes['custom_data'] = customData
      docendo.updateUsers(this.schoolId, user.id, changes, data => {
        this.isUpdatingNumber = false
        this.closeEditing()
        this.$store.commit('updateSchoolUser', data)
      }, failure => {
        console.log('failure')
      })
    },
    debounce (func, delay) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(func, delay)
    },
    setPrevWeek () {
      const date = moment(this.startDate).subtract(1, 'week')
      this.startDate = moment(date).startOf('isoWeek')
      this.endDate = moment(date).endOf('isoWeek')
      this.readEvents()
    },
    setNextWeek () {
      const date = moment(this.startDate).add(1, 'week')
      this.startDate = moment(date).startOf('isoWeek')
      this.endDate = moment(date).endOf('isoWeek')
      this.readEvents()
    },
    setThisWeek () {
      this.startDate = moment().startOf('isoWeek')
      this.endDate = moment().endOf('isoWeek')
      this.readEvents()
    },
    setStartDate (date) {
      this.setDates(date, null)
    },
    setEndDate (date) {
      this.setDates(null, date)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.readEvents()
    },
    placeEventsOnUsers () {
      this.eventsInUserCalendarIdHash = {}

      // Sørg for at 'available' behandles før vikartimer
      const eventsSorted = this.events.slice().sort((a, b) => {
        if (a.event_type === 'available' && b.event_type !== 'available') return -1
        if (b.event_type === 'available' && a.event_type !== 'available') return 1
        return 0
      })

      eventsSorted.forEach(event => {
        if (event.event_type === 'presence') {
          const calcMinutes = moment(event.end).diff(event.start, 'minutes')
          if (!this.eventsInUserCalendarIdHash[event.calendar_id]) {
            this.eventsInUserCalendarIdHash[event.calendar_id] = {}
          }
          if (this.eventsInUserCalendarIdHash[event.calendar_id]['presence']) {
            this.eventsInUserCalendarIdHash[event.calendar_id]['presence'] += calcMinutes
          } else {
            this.eventsInUserCalendarIdHash[event.calendar_id]['presence'] = calcMinutes
          }
          return
        }

        if (event.school_id !== this.schoolId) {
          return
        }

        event.calendars.forEach(calendarEvent => {
          if (calendarEvent.calendar_type !== 'user') {
            return
          }

          const calendarId = calendarEvent.calendar_id
          if (!this.eventsInUserCalendarIdHash[calendarId]) {
            this.eventsInUserCalendarIdHash[calendarId] = {}
          }

          // Gem alle 'available' events pr. bruger som liste, så vi kan tjekke overlap senere
          if (!calendarEvent.substitute && event.event_type === 'available') {
            if (!this.eventsInUserCalendarIdHash[calendarId]['available']) {
              this.eventsInUserCalendarIdHash[calendarId]['available'] = [event]
              this.eventsInUserCalendarIdHash[calendarId]['available_count'] = calendarEvent.minutes
            } else {
              const idxAvail = this.eventsInUserCalendarIdHash[calendarId]['available'].findIndex(e => e.event_id === event.event_id)
              if (idxAvail < 0) {
                this.eventsInUserCalendarIdHash[calendarId]['available'].push(event)
                this.eventsInUserCalendarIdHash[calendarId]['available_count'] += calendarEvent.minutes
              }
            }
            return
          }

          // Standard ikke-vikar event (undtagen 'available' som er håndteret ovenfor)
          if (!calendarEvent.substitute) {
            const propName = `${event.event_type}_count`
            if (!this.eventsInUserCalendarIdHash[calendarId][event.event_type]) {
              this.eventsInUserCalendarIdHash[calendarId][event.event_type] = [event]
              this.eventsInUserCalendarIdHash[calendarId][propName] = calendarEvent.minutes
            } else {
              const idx = this.eventsInUserCalendarIdHash[calendarId][event.event_type].findIndex(userEvent => userEvent.event_id === event.event_id)
              if (idx < 0) {
                this.eventsInUserCalendarIdHash[calendarId][event.event_type].push(event)
                this.eventsInUserCalendarIdHash[calendarId][propName] += calendarEvent.minutes
              }
            }
            return
          }

          // VIKAR: fratræk overlap mod 'available' for samme bruger
          const teachingTypes = ['subject', 'person_resource', 'person_resource_visible', 'optional']
          const isTeaching = teachingTypes.indexOf(event.event_type) > -1
          const availableList = this.eventsInUserCalendarIdHash[calendarId] && this.eventsInUserCalendarIdHash[calendarId]['available']
            ? this.eventsInUserCalendarIdHash[calendarId]['available']
            : []

          let overlap = 0
          availableList.forEach(av => {
            overlap += this.computeOverlapMinutes(event.start, event.end, av.start, av.end)
          })

          const effectiveMinutes = Math.max(0, calendarEvent.minutes - overlap)

          if (isTeaching) {
            if (effectiveMinutes > 0) {
              if (!this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching']) {
                this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching'] = [event]
                this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching_count'] = effectiveMinutes
              } else {
                const idxST = this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching'].findIndex(userEvent => userEvent.event_id === event.event_id)
                if (idxST < 0) {
                  this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching'].push(event)
                  this.eventsInUserCalendarIdHash[calendarId]['substitute_teaching_count'] += effectiveMinutes
                }
              }
            }
          } else {
            if (effectiveMinutes > 0) {
              if (!this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching']) {
                this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching'] = [event]
                this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching_count'] = effectiveMinutes
              } else {
                const idxSNT = this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching'].findIndex(userEvent => userEvent.event_id === event.event_id)
                if (idxSNT < 0) {
                  this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching'].push(event)
                  this.eventsInUserCalendarIdHash[calendarId]['substitute_not_teaching_count'] += effectiveMinutes
                }
              }
            }
          }
        })
      })
    },
    readEvents () {
      if (this.filteredCalendars.length < 1) {
        return
      }
      this.clickCount++
      const start = this.startDate
      const end = this.endDate
      const calendarIds = this.filteredCalendars
      if (this.clickCount === 2) {
        clearTimeout(this.clickCountTimer)
      }
      if (this.clickCount === 1) {
        this.isLoading = true
        docendo.calendarEvents2(calendarIds, start, end, null,
          success => {
            this.events = success && success.length > 0 ? JSON.parse(success) : []
            this.placeEventsOnUsers()
            this.isLoading = false
          }, failure => {
            console.log('failure', failure)
          })
        this.clickCountTimer = setTimeout(() => {
          this.clickCount = 0
        }, 300)
      } else {
        this.debounce(() => {
          this.isLoading = true
          docendo.calendarEvents2(calendarIds, start, end, null,
            success => {
              this.events = success && success.length > 0 ? JSON.parse(success) : []
              this.placeEventsOnUsers()
              this.isLoading = false
            }, failure => {
              console.log('failure', failure)
            })
          this.clickCount = 0
        }, 300)
      }
    }
  },
  mounted () {
    this.readEvents()
  },
  beforeDestroy () {
    if (this.$refs.popper_child) {
      this.$refs.popper_child.remove()
    }
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.basis-overview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.basis-overview__dates {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.basis-overview__body {
  position: relative;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
  border: 1px solid #d8d8d8;
}

.d-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 4px;
}

.d-table > thead {
  font-weight: bold;
  text-align: left;
  border-radius: 4px 4px 0 0;
  border-bottom: 1px solid #d8d8d8
}

.d-table > thead > tr > th:first-child {
  border-radius: 4px 4px 0 0;
}

.d-table > thead > tr > th {
  border-collapse: collapse;
  border-bottom: 1px solid #d8d8d8;
  border-right: 1px solid #d8d8d8;
  padding: 4px;
  text-align: center;
}

.d-table > thead > tr > th, .d-table > tfoot > tr > td {
  padding: 5px;
}

.d-table > thead > tr:nth-child(2) {
  font-size: 11px;
}

.d-table > tbody > tr {
  border-bottom: 1px solid #d8d8d8;
  height: 50px;
}

.d-table > tbody > tr > td:nth-child(n+3) {
  text-align: right;
  padding: 0 8px;
  width: 100px;
}

.d-table > tbody > tr > td:nth-child(3) {
  position: relative;
  cursor: pointer;
}

.d-table > tbody > tr > td:nth-child(3):hover {
  text-decoration: underline;
  color: var(--primary);
}

.d-table tr th, .d-table > tfoot tr td {
  padding: 2px 4px;
}

.d-table > tbody > tr > td:nth-child(1) {
  padding-left: 1rem;
}

.d-table > tbody > tr > td:nth-child(2) {
  padding-left: 1rem;
  font-weight: bold;
}

.d-table tbody tr td:nth-child(3) {
  background: rgba(153, 197, 244, .1);
}

.d-table tbody tr td:nth-child(4),
.d-table tbody tr td:nth-child(5) {
  background: rgba(122, 115, 255, .1);
}

.d-table tbody tr td:nth-child(6) {
  background: rgba(255, 217, 106, .1);
}

.d-table tbody tr td:nth-child(7) {
  background: #fef7f5;
}

.d-table tbody tr td:nth-child(11) {
  background: #f1f1f1;
}

.d-table tbody tr td:nth-child(8),
.d-table tbody tr td:nth-child(9) {
  background: rgba(109, 208, 215, .1);
}


.do-button__multiple {
  display: flex;
  justify-content: stretch;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, .12) 0px 1px 1px 0px, rgba(64, 68, 82, .16) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(64, 68, 82, .08) 0px 2px 5px 0px;
  border: 0;
  background: #fff;
  font-weight: 600;
  letter-spacing: .025em;
  border-radius: 4px;
}

.do-button__multiple > .do-button__multiple__bttn {
  text-align: center;
  padding: 2px 10px;
  font-weight: bold !important;
  height: 28px;
  cursor: pointer;
  background: #ffffff;
  border: none;
}

.do-button__multiple > .do-button__multiple__bttn:hover {
  background: #f9fafb;
}

.do-button__multiple > .do-button__multiple__bttn:focus {
  position: relative;
  z-index: 1;
}

.do-button__multiple > .do-button__multiple__bttn:nth-child(2) {
  border: 1px solid #ccc;
  border-radius: 0;
  border-width: 0 1px;
}

.do-button__multiple > .do-button__multiple__bttn.bttn--disabled {
  cursor: not-allowed;
}


.number__input-opened {
  position: absolute;
  top: 10%;
  left: 10%;
  display: flex;
  align-items: stretch;
  z-index: 1;
  background: #ffffff;
  padding: 0;
  box-shadow: 0 1px 3px 0 rgb(27 36 50 / 15%);
}

.number__input-opened input {
  width: 100px !important;
  border-radius: 4px 0 0 4px;
}

.number__input-opened button {
  height: auto;
  border-radius: 0 4px 4px 0;
  width: 65px !important;
}

.scale-enter-active {
  transition: transform 0.15s;
}

.scale-enter, .scale-leave-to {
  transform: scale(0.5);
}

.bttn.bttn--primary.bttn--primary--spinner {
  background-color: #edf0f5 !important;
}


</style>



// WEBPACK FOOTER //
// BasisOverview.vue?bf5d366e